import React, { useState, useCallback, useMemo } from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';

import useRoleApi from './config';
import DataTable from '../../components/ui/DataTable';
import GenericFormDrawer from '../../components/GenericFormDrawer';
import ConfirmDialog from '../../components/ui/ConfirmDialog';
import OptionsMenu from '../../components/ui/OptionsMenu';

const Container = styled.div``;

export default function RolesPage() {
  const { t } = useTranslation();
  const history = useHistory();

  const [drawerOpen, setDrawerOpen] = useState(false);
  const [selectedRow, setSelectedRow] = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState({ isOpen: false, ids: [], name: '' });
  const [deleting, setDeleting] = useState(false);

  const {
    objectSchema, fetching, columns: baseColumns, data,
    create, update, _delete,
  } = useRoleApi();

  const handleNewButtonClick = useCallback(() => {
    setSelectedRow(null);
    setDrawerOpen(true);
  }, []);

  const handleEdit = useCallback((row) => {
    setSelectedRow(row);
    setDrawerOpen(true);
  }, []);

  const handleSave = useCallback(async (formData) => {
    const success = selectedRow
      ? await update(selectedRow.id, formData)
      : await create(formData);
    if (success) setDrawerOpen(false);
    return success;
  }, [selectedRow, create, update]);

  const handleDelete = useCallback((row) => {
    setDeleteConfirm({
      isOpen: true,
      ids: [row.id],
      name: row.name,
    });
  }, []);

  const handleDeleteConfirm = useCallback(async () => {
    setDeleting(true);
    await _delete(deleteConfirm.ids);
    setDeleteConfirm({ isOpen: false, ids: [], name: '' });
    setDeleting(false);
  }, [_delete, deleteConfirm.ids]);

  const handleRowClick = useCallback((row) => {
    history.push(`/roles/${row.id}`, { role: row });
  }, [history]);

  const actionOptions = useMemo(() => [
    { type: 'details', label: t('employee.detail'), onClick: (row) => history.push(`/roles/${row.id}`, { role: row }) },
    { type: 'edit', label: t('role.edit'), onClick: handleEdit },
    { type: 'divider' },
    { type: 'delete', label: t('common.delete'), onClick: handleDelete },
  ], [t, handleEdit, handleDelete, history]);

  const columns = useMemo(() => {
    return [
      ...baseColumns,
      {
        name: 'actions',
        label: ' ',
        options: {
          filter: false, sort: false, download: false, print: false,
          customBodyRender: (_, tableMeta) => {
            const row = data[tableMeta.rowIndex];
            return <OptionsMenu actions={actionOptions} row={row} />;
          },
        },
      },
    ];
  }, [baseColumns, actionOptions, data]);

  return (
    <Container>
      <GenericFormDrawer
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        title={selectedRow ? t('role.edit') : t('role.new')}
        schema={objectSchema}
        initialValues={selectedRow}
        onSave={handleSave}
      />

      <ConfirmDialog
        isOpen={deleteConfirm.isOpen}
        title={t('role.deleteTitle')}
        message={t('role.deleteMessage', { name: deleteConfirm.name })}
        onConfirm={handleDeleteConfirm}
        onCancel={() => setDeleteConfirm({ isOpen: false, ids: [], name: '' })}
        loading={deleting}
      />

      {useMemo(() => (
        <DataTable
          title={t('role.title')}
          fetching={fetching}
          columns={columns}
          data={data}
          totalRowCount={data.length}
          pageIndex={0}
          pageSize={data.length || 10}
          canCreateNewRecord={true}
          onNewButtonClick={handleNewButtonClick}
          onRowClick={handleRowClick}
          serverSide={false}
        />
      ), [
        t, fetching, columns, data,
        handleNewButtonClick, handleRowClick,
      ])}
    </Container>
  );
}
